import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { useAuth } from '../auth.js';
import { t } from '../i18n.js';

type Step = 'password' | 'tado' | 'done';

export function SetupWizard() {
  const { refresh } = useAuth();
  const [step, setStep] = useState<Step>('password');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [tadoInfo, setTadoInfo] = useState<{ url: string; code: string } | null>(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => { void api.getStatus().then((s) => { if (s.install_complete) setStep('tado'); }); }, []);

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (pw1.length < 8) { setErr(t('setup.error_min_length')); return; }
    if (pw1 !== pw2) { setErr(t('setup.error_mismatch')); return; }
    try { await api.setupPassword(pw1); await api.login(pw1); setStep('tado'); }
    catch { setErr(t('setup.error_set')); }
  };

  const startTado = async () => {
    setErr(null);
    try {
      const info = await api.startTado();
      setTadoInfo({ url: info.verification_uri_complete, code: info.user_code });
      setPolling(true);
      const intervalMs = (info.interval || 5) * 1000;
      const tick = async () => {
        try {
          const r = await api.pollTado();
          if (r.status === 'connected') {
            await api.finalizeTado();
            setPolling(false); setStep('done'); await refresh(); return;
          }
          setTimeout(tick, intervalMs);
        } catch { setTimeout(tick, intervalMs); }
      };
      setTimeout(tick, intervalMs);
    } catch { setErr(t('setup.error_tado')); }
  };

  return (
    <div className="auth-shell" style={{ maxWidth: 480 }}>
      <div className="card">
        <h2 style={{ margin: '0 0 12px' }}>{t('setup.title')}</h2>
        {step === 'password' && (
          <form onSubmit={submitPassword}>
            <p className="muted">{t('setup.choose_password')}</p>
            <div className="field"><label className="label">{t('setup.password_min')}</label><input type="password" value={pw1} onChange={(e) => setPw1(e.target.value)} /></div>
            <div className="field"><label className="label">{t('setup.confirm')}</label><input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} /></div>
            {err && <div style={{ color: 'var(--danger)', marginBottom: 8 }}>{err}</div>}
            <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>{t('setup.continue')}</button>
          </form>
        )}
        {step === 'tado' && (
          <div>
            <p className="muted">{t('setup.connect_tado')}</p>
            {!tadoInfo && <button className="btn btn-primary" onClick={() => void startTado()}>{t('setup.start_tado')}</button>}
            {tadoInfo && (
              <div>
                <p>{t('setup.open_url')} <a href={tadoInfo.url} target="_blank" rel="noreferrer">{tadoInfo.url}</a></p>
                <p>{t('setup.confirm_code')} <strong>{tadoInfo.code}</strong></p>
                <p className="muted">{polling ? t('setup.waiting') : t('setup.finishing')}</p>
              </div>
            )}
            {err && <div style={{ color: 'var(--danger)', marginTop: 8 }}>{err}</div>}
          </div>
        )}
        {step === 'done' && <p>{t('setup.complete')}</p>}
      </div>
    </div>
  );
}
